import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";
import SEO, { breadcrumbJsonLd, SITE } from "../components/SEO";
import { findConsultant, CONSULTANTS } from "../data/consultants";

export default function ConsultantDetail() {
  const { slug } = useParams();
  const c = findConsultant(slug);
  const jsonLd = useMemo(() => (c ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Physician",
        "@id": `${SITE}/consultants/${c.slug}#person`,
        url: `${SITE}/consultants/${c.slug}`,
        name: c.name,
        jobTitle: c.title,
        image: `${SITE}${c.image}`,
        sameAs: [...(c.sameAs || []), ...(c.phinUrl ? [c.phinUrl] : []), ...(c.externalProfile ? [c.externalProfile.url] : [])],
        affiliation: [
          ...((c.sameAs || []).some((u) => u.includes("cam.ac.uk")) ? [{ "@type": "CollegeOrUniversity", name: "University of Cambridge", url: "https://www.cam.ac.uk/" }] : []),
          ...((c.sameAs || []).some((u) => u.includes("cuh.nhs.uk")) ? [{ "@type": "Hospital", name: "Cambridge University Hospitals NHS Foundation Trust", url: "https://www.cuh.nhs.uk/" }] : []),
        ],
        medicalSpecialty: c.slug === "navazh-jalaludeen" ? "Cardiovascular" : ["Cardiovascular", "Endocrine"],
        knowsAbout: c.specialties,
        address: { "@type": "PostalAddress", addressLocality: "Cambridge", postalCode: "CB2 0QQ", addressCountry: "GB" },
        description: c.bio,
        worksFor: { "@type": "MedicalClinic", name: "Cambridge Cardiovascular Prevention Clinic" },
      },
      breadcrumbJsonLd([
        { name: "Consultants", path: "/consultants" },
        { name: c.name, path: `/consultants/${c.slug}` },
      ]),
    ],
  } : null), [c]);
  if (!c) {
    return (
      <div className="container-ccc py-24">
        <p>Consultant not found.</p>
        <Link to="/consultants" className="text-bronze-dark">← Back</Link>
      </div>
    );
  }
  const others = CONSULTANTS.filter((x) => x.slug !== slug);
  return (
    <div data-testid={`consultant-detail-${c.slug}`}>
      <SEO
        title={`${c.name} - ${c.slug === "navazh-jalaludeen" ? "Echocardiography" : "Consultant Physician"}, Cambridge`}
        description={`${c.name} — ${c.title} at Cambridge Cardiovascular Prevention Clinic. ${c.bio}`}
        path={`/consultants/${c.slug}`}
        image={c.image}
        type="profile"
        jsonLd={jsonLd}
      />
      <section className="container-ccc py-16">
        <Link to="/consultants" className="inline-flex items-center gap-1 text-ink-muted hover:text-bronze-dark text-sm"><CaretLeft size={14}/> All consultants</Link>
        <div className="mt-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-md">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow">{c.title}</div>
            <h1 className="font-serif text-4xl lg:text-5xl mt-3 leading-[1.05]">{c.name}</h1>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.specialties.map((sp) => (
                <span key={sp} className="text-[11px] uppercase tracking-widest text-bronze-dark border border-bronze-light/60 rounded-full px-2.5 py-0.5">{sp}</span>
              ))}
            </div>
            <div className="mt-8 text-ink-muted leading-relaxed whitespace-pre-line">{c.bio}</div>
            <div className="mt-8 p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
              <div className="eyebrow">Qualifications & roles</div>
              <p className="mt-3 text-sm text-ink-muted">{c.training}</p>
              {c.gmc && <p data-testid="consultant-gmc" className="mt-3 text-sm text-ink">GMC number: {c.gmc}</p>}
              {c.phinUrl && <a data-testid="consultant-phin" href={c.phinUrl} target="_blank" rel="noopener noreferrer"
                className="mt-2 inline-block text-sm underline text-bronze-dark">View PHIN profile (phin.org.uk)</a>}
              {(c.sameAs || []).map((u) => (
                <a key={u} data-testid={`consultant-sameas-${u.includes("cuh.nhs.uk") ? "cuh" : "cam"}`} href={u} target="_blank" rel="noopener noreferrer"
                  className="mt-2 block text-sm underline text-bronze-dark">{u.includes("cuh.nhs.uk") ? "Cambridge University Hospitals staff profile" : "University of Cambridge profile"}</a>
              ))}
              {c.externalProfile && <a data-testid="consultant-external-profile" href={c.externalProfile.url} target="_blank" rel="noopener noreferrer"
                className="mt-2 block text-sm underline text-bronze-dark">{c.externalProfile.label}</a>}
            </div>
            {c.research && (
              <div data-testid="consultant-research" className="mt-6 p-6 bg-white border-l-2 border-bronze-dark rounded-md">
                <div className="eyebrow">Research & academic profile</div>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{c.research}</p>
              </div>
            )}
            {c.bookable !== false && (
              <Link
                data-testid="consultant-detail-enquiry-cta"
                to={`/enquiry?consultant=${c.slug}`}
                className="btn-primary mt-10"
              >
                Book with {c.name.split(" ").slice(-1)[0]}
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 border-t border-bronze-light/30">
        <div className="container-ccc py-16">
          <div className="eyebrow">Also at the clinic</div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/consultants/${o.slug}`} className="group block bg-white border border-stone-200/60 rounded-md overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={o.image} alt={o.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="font-serif text-xl">{o.name}</div>
                  <div className="eyebrow mt-2">{o.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
